import React from 'react';
import Icon from '../../../components/AppIcon';

const RiskIndicators = ({ structure, controls, capacity }) => {
  const calculateStructureScore = () => {
    if (!structure?.positions || structure?.positions?.length === 0) return 0;
    
    const filledPositions = structure?.positions?.filter(p => 
      p?.title && p?.department && p?.level && p?.holder && p?.experience && p?.responsibilities
    )?.length;
    
    return Math.round((filledPositions / structure?.positions?.length) * 100);
  };

  const calculateControlScore = () => {
    let totalControls = 0;
    let effectiveControls = 0;

    Object.keys(controls)?.forEach(area => {
      Object.keys(controls?.[area])?.forEach(control => {
        if (controls?.[area]?.[control]?.implemented) {
          totalControls++;
          const effectiveness = controls?.[area]?.[control]?.effectiveness;
          if (effectiveness === 'excellent' || effectiveness === 'good') {
            effectiveControls++;
          }
        }
      });
    });

    return totalControls > 0 ? Math.round((effectiveControls / totalControls) * 100) : 0;
  };

  const calculateCapacityScore = () => {
    const areas = ['technical', 'managerial', 'financial', 'strategic'];
    let totalScore = 0;
    let filledAreas = 0;

    areas?.forEach(area => {
      if (capacity?.[area]?.level) {
        filledAreas++;
        const levelScores = {
          expert: 100,
          advanced: 80,
          intermediate: 60,
          basic: 40,
          novice: 20
        };
        totalScore += levelScores?.[capacity?.[area]?.level] || 0;
      }
    });

    return filledAreas > 0 ? Math.round(totalScore / filledAreas) : 0;
  };

  const structureScore = calculateStructureScore();
  const controlScore = calculateControlScore();
  const capacityScore = calculateCapacityScore();
  const overallScore = Math.round((structureScore + controlScore + capacityScore) / 3);

  const getRiskLevel = (score) => {
    if (score >= 80) return { level: 'Rendah', color: 'success', icon: 'CheckCircle2' };
    if (score >= 60) return { level: 'Sedang', color: 'warning', icon: 'AlertCircle' };
    return { level: 'Tinggi', color: 'error', icon: 'XCircle' };
  };

  const overallRisk = getRiskLevel(overallScore);
  const structureRisk = getRiskLevel(structureScore);
  const controlRisk = getRiskLevel(controlScore);
  const capacityRisk = getRiskLevel(capacityScore);

  const indicators = [
    {
      title: 'Kelengkapan Struktur',
      score: structureScore,
      risk: structureRisk,
      description: 'Tingkat kelengkapan pemetaan struktur organisasi'
    },
    {
      title: 'Efektivitas Kontrol',
      score: controlScore,
      risk: controlRisk,
      description: 'Persentase sistem kontrol yang efektif'
    },
    {
      title: 'Kapasitas Manajemen',
      score: capacityScore,
      risk: capacityRisk,
      description: 'Level kompetensi tim manajemen keseluruhan'
    }
  ];

  return (
    <div className="card sticky top-44 lg:top-36">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold mb-2">Indikator Risiko Tata Kelola</h3>
          <p className="text-sm text-muted-foreground">Real-time assessment</p>
        </div>
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
        </div>
      </div>
      <div className="mb-6 p-4 md:p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-muted-foreground">Skor Keseluruhan</span>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-${overallRisk?.color}/10`}>
            <Icon name={overallRisk?.icon} size={16} color={`var(--color-${overallRisk?.color})`} />
            <span className={`text-sm font-medium text-${overallRisk?.color}`}>
              Risiko {overallRisk?.level}
            </span>
          </div>
        </div>
        <div className="flex items-end gap-3">
          <span className="text-4xl md:text-5xl font-bold text-primary data-text">
            {overallScore}
          </span>
          <span className="text-2xl md:text-3xl font-semibold text-muted-foreground mb-1">/100</span>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Skor kesiapan tata kelola untuk scale-up
        </p>
      </div>
      <div className="space-y-4">
        {indicators?.map((indicator, index) => (
          <div key={index} className="p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm md:text-base font-semibold mb-1">{indicator?.title}</h4>
                <p className="text-xs text-muted-foreground">{indicator?.description}</p>
              </div>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-${indicator?.risk?.color}/10 flex-shrink-0 ml-2`}>
                <Icon name={indicator?.risk?.icon} size={14} color={`var(--color-${indicator?.risk?.color})`} />
                <span className={`text-xs font-medium text-${indicator?.risk?.color}`}>
                  {indicator?.risk?.level}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-${indicator?.risk?.color} transition-all duration-500`}
                  style={{ width: `${indicator?.score}%` }}
                />
              </div>
              <span className="text-sm font-bold data-text w-12 text-right">
                {indicator?.score}%
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={18} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-medium text-accent-foreground mb-1">Interpretasi Skor</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• 80-100: Tata kelola kuat, siap scale-up</li>
              <li>• 60-79: Perlu perbaikan minor</li>
              <li>• &lt;60: Risiko tinggi, perlu perbaikan signifikan</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskIndicators;